/** @format */

import { formatSeries } from "../utils/helpers";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { PaperEntry, countEntryParts } from "../utils/paper";
import { UndoDelete } from "./UndoDelete";
import { Locale } from "../features/i18n/init";
import { trackEvent } from "../utils/analytics";

interface ExamPreviewProps {
	addedQuestions: PaperEntry[];
	onRemoveQuestion?: (question: PaperEntry) => void;
	onClearAll?: () => void;
	onEditQuestion?: (question: PaperEntry) => void;
}

export const ExamPreview = ({ addedQuestions, onRemoveQuestion, onClearAll, onEditQuestion }: ExamPreviewProps) => {
	const { t } = useTranslation();
	const { locale } = useParams<{ locale: Locale }>();
	const location = useLocation();

	const [isOpen, setIsOpen] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<string[]>([]);

	const totalMarks = addedQuestions.reduce((sum, q) => sum + (q.totalMarks ?? 0), 0);
	const duration = totalMarks;

	const handleUndo = (questionId: string) => {
		setPendingDelete((prev) => prev.filter((id) => id !== questionId));
	};

	const handleExpire = (question: PaperEntry) => {
		trackEvent("remove_from_paper", { question_id: question.parentQuestionId });
		onRemoveQuestion?.(question);
		setPendingDelete((prev) => prev.filter((id) => id !== question.parentQuestionId));
	};

	useEffect(() => {
		if (addedQuestions.length > 0) {
			setIsOpen(true);
		}
	}, [addedQuestions.length]);

	return (
		<div
			data-testid="exam-preview"
			className="fixed bottom-0 left-0 right-0 md:left-auto md:w-100 md:mr-2 md:mb-11.25 bg-white rounded-t-lg z-30 shadow-[-4px_0px_8px_0px_#00000040]"
		>
			<button
				type="button"
				onClick={() => setIsOpen((isCurrentlyOpen) => !isCurrentlyOpen)}
				className="w-full flex h-11.25 items-center justify-between px-6 py-2.5 cursor-pointer bg-(--public-color-menu-label) text-white font-lato text-[14px] rounded-tl-[10px] rounded-tr-[10px]"
			>
				<span className="font-roboto">
					{t("examPreview.questionsCount")} - <span className="font-bold text-[13px]">{addedQuestions.length}</span>
				</span>
				<span className="font-roboto">
					{t("examPreview.duration")} -{" "}
					<span className="font-bold text-[13px]">
						{duration}
						<span className="text-[12px]"> ({t("examPreview.minutes")})</span>
					</span>
				</span>
				<span className="font-roboto">
					{t("examPreview.marksCount")} - <span className="font-bold text-[13px]">{totalMarks}</span>
				</span>
				{isOpen ? (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={2}
						stroke="currentColor"
						className="size-6"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
					</svg>
				) : (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={2}
						stroke="currentColor"
						className="size-6"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
					</svg>
				)}
			</button>
			{isOpen && (
				<>
					<div className="flex flex-col max-h-[60vh] overflow-y-auto" data-testid="exam-preview-items">
						{addedQuestions.map((question) => {
							const label =
								`${t("examPreview.question")}${question.rootQuestionNumber} - ${formatSeries(question.series) ?? ""}`.trim();
							const parts = countEntryParts(question);
							return (
								<div
									key={question.parentQuestionId}
									className="flex flex-row items-center justify-between py-5.25 px-6.25 w-full h-18 bg-white border border-(--public-color-border-default) text-(--public-color-text-default) font-roboto text-[16px]"
								>
									{pendingDelete.includes(question.parentQuestionId) ? (
										<UndoDelete
											onCancel={() => handleUndo(question.parentQuestionId)}
											duration={8}
											onExpire={() => handleExpire(question)}
										/>
									) : (
										<>
											<div>
												<span
													data-testid={`exam-preview-question-${question.rootQuestionNumber}`}
													className="align-middle"
												>
													{label}
												</span>{" "}
												{parts.total > 0 && (
													<span data-testid="parts-count" className="text-[13px] font-roboto align-middle">
														({t("examPreview.partsLabel", { count: parts.selected, total: parts.total })})
													</span>
												)}
											</div>

											<div className="flex flex-row gap-4">
												<Link
													onClick={() => onEditQuestion?.(question)}
													to={`/${locale ?? "en"}/questions/${question.parentQuestionId}`}
													state={{ backgroundLocation: location }}
													data-testid={`edit-question-${question.rootQuestionNumber}`}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														fill="none"
														viewBox="0 0 24 24"
														strokeWidth={1.5}
														stroke="currentColor"
														className="size-5"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
														/>
													</svg>
												</Link>
												<button
													data-testid="btn-remove-added-question"
													type="button"
													aria-label={t("examPreview.deleteAddedQuestion")}
													onClick={() => setPendingDelete((prev) => [...prev, question.parentQuestionId])}
													className="cursor-pointer"
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														fill="none"
														viewBox="0 0 24 24"
														strokeWidth={1.5}
														stroke="currentColor"
														className="size-5"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
														/>
													</svg>
												</button>
											</div>
										</>
									)}
								</div>
							);
						})}
					</div>

					<div className="flex flex-row w-full h-12 border border-(--public-color-border-default)">
						<button
							type="button"
							aria-label={t("examPreview.clearAllQuestions")}
							onClick={onClearAll}
							className="flex w-[35%] p-5 gap-2 items-center justify-center cursor-pointer bg-(--public-color-page-bg) text-(--public-color-btn-clear-text) font-roboto text-[18px] leading-[120%]"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="#C21200"
								className="size-5"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
								/>
							</svg>
							{t("examPreview.clear")}
						</button>
						<Link
							data-testid="build-paper-link"
							to={`/${locale}/generated-paper${globalThis.location.search}`}
							onClick={() =>
								trackEvent("build_paper", { questions_count: addedQuestions.length, total_marks: totalMarks })
							}
							className="flex w-[65%] p-5 gap-2.5 items-center justify-center bg-black text-white text-[18px] font-roboto leading-[120%]"
						>
							{t("examPreview.buildPaper")}
						</Link>
					</div>
				</>
			)}
		</div>
	);
};
